import { DOCUMENT, isPlatformServer } from '@angular/common';
import { computed, effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { getAllRouteData } from '../lib/get-all-route-data';
import { StorageService } from './storage.service';
import { TransferStateService } from './transfer-state.service';

export type ThemePreference = 'light' | 'dark' | 'system';

const THEME_STORAGE_KEY = 'app-theme-preference';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private storage = inject(StorageService);
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private route = inject(ActivatedRoute, { optional: true });
  private transferState = inject(TransferStateService);

  readonly preference = signal<ThemePreference>('system');
  private readonly systemPrefersDark = signal(false);

  private readonly routeForcedThemeSsr = toSignal(
    this.transferState.withStateTransferObservable('route-forced-theme', () => {
      return this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          const route = this.route?.snapshot;
          if (!route) return undefined;
          return getAllRouteData(route).forcedTheme as 'light' | 'dark' | undefined;
        })
      );
    })
  );

  readonly routeForcedTheme = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        const route = this.route?.snapshot;
        if (!route) return undefined;
        return (getAllRouteData(route).forcedTheme as 'light' | 'dark' | undefined) ?? null;
      })
    )
  );

  readonly resolvedThemeWithoutForced = computed<'light' | 'dark'>(() => {
    const preference = this.preference();
    const prefersDark = this.systemPrefersDark();
    if (preference === 'system') return prefersDark ? 'dark' : 'light';
    return preference;
  });

  readonly resolvedTheme = computed<'light' | 'dark'>(() => {
    const resolvedThemeWithoutForced = this.resolvedThemeWithoutForced();
    const forcedTheme = this.routeForcedTheme();
    const routeForcedThemeSsr = this.routeForcedThemeSsr();
    if (forcedTheme) return forcedTheme;
    if (forcedTheme === undefined) return routeForcedThemeSsr;
    return resolvedThemeWithoutForced;
  });

  readonly darkTheme = computed(() => this.resolvedTheme() === 'dark');
  readonly lightTheme = computed(() => this.resolvedTheme() === 'light');

  constructor() {
    effect(() => {
      const theme = this.resolvedTheme();
      const classList = this.document.body.classList;
      classList.toggle('theme-dark', theme === 'dark');
      classList.toggle('theme-light', theme === 'light');
    });

    if (isPlatformServer(this.platformId)) return;

    const stored = this.storage.get<ThemePreference>(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      this.preference.set(stored);
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    this.systemPrefersDark.set(media.matches);
    media.addEventListener('change', event => this.systemPrefersDark.set(event.matches));
  }

  setPreference(preference: ThemePreference) {
    this.preference.set(preference);
    this.storage.set(THEME_STORAGE_KEY, preference);
  }
}
